import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@lib/utils';

/**
 * The blue -> teal border-box half of the dual background-clip trick - the ONE source both fills
 * compose with, so the border gradient is defined once (retune it here and every surface follows).
 */
const BORDER_GRADIENT = 'linear-gradient(to right, #9BBDFF, #3EC6C6) border-box';

/**
 * The gradient border rendered via the background-clip trick: a surface fill on `padding-box` layered
 * under `BORDER_GRADIENT` on `border-box`, so the 2px transparent border shows the gradient. Tailwind
 * can't express a dual background-clip, so it rides in an inline `style` - the one bit of this
 * treatment classes can't do. Hoisted to avoid re-creation; this is the default (white) fill, so
 * callers that pass no `fill` share this exact object.
 */
const gradientBorderStyle = {
  background: `linear-gradient(white, white) padding-box, ${BORDER_GRADIENT}`,
} as const;

/** The same treatment with a caller-chosen padding-box fill (default white stays the hoisted object
 *  above, so existing surfaces are untouched). */
function fillStyle(fill: string): CSSProperties {
  return {
    background: `linear-gradient(${fill}, ${fill}) padding-box, ${BORDER_GRADIENT}`,
  };
}

/**
 * A surface framed by the blue -> teal gradient border. Single source for the treatment shared by
 * `ProfileCompletionModal`'s info card, the match-state nudge card, and the event match-gate - retune
 * the gradient here, never at a call site, so the surfaces stay identical. Callers pass only
 * layout/radius/spacing classes; the border (`border-2 border-transparent`) is fixed. The fill
 * defaults to white; the event match-gate passes the Figma's offwhite #f0efeb so its white inner body
 * reads as an inset card against the frame.
 */
export function GradientBorderBox({
  className,
  children,
  fill,
}: {
  className?: string;
  children: ReactNode;
  /** Padding-box surface color inside the gradient border. Omit for the default white treatment. */
  fill?: string;
}) {
  return (
    <div
      className={cn('border-2 border-transparent', className)}
      style={fill ? fillStyle(fill) : gradientBorderStyle}
    >
      {children}
    </div>
  );
}
